import { useMemo, useEffect, useState, useCallback, useContext } from 'react';

import { useLazyQuery } from '@apollo/client';

import { pause } from 'async-timing';

import { getClient } from '../apollo';
import * as WHITELABELS from '../config/whitelabels';
import LaunchDarklyContext from '../contexts/LaunchDarklyContext';
import { getContentBlocks } from '../graphql/content';
import { getWhitelabels } from '../graphql/query';
import { WhiteLabelType } from '../types/WhiteLabelType';
import { BlockType } from '../types/blocks/BlockInterface';
import { useWhitelabelByIndexHTML } from './useWhitelabelByIndexHTML';

const fetchPolicy = 'network-only';

export default function useAppData({ defaultInstance, findWhiteLabel }: any) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isBlockingLoad, setIsBlockingLoad] = useState(false);
  const [isThemeBlockingLoad, setIsThemeBlockingLoad] = useState(false);
  const [whitelabel, setWhitelabel] = useState<Partial<WhiteLabelType>>({});
  const { client: lDClient } = useContext(LaunchDarklyContext);

  // gets all the content blocks required for the app to work.
  const [fetchBlocks, blocksResult] = useLazyQuery(getContentBlocks, {
    fetchPolicy,
    client: getClient(),
  });

  // gets all the whitelabels required for the app to work
  const [
    fetchWhiteLabels,
    { data, loading: whitelabelsLoading, called, error },
  ] = useLazyQuery(getWhitelabels, {
    fetchPolicy,
    client: getClient(),
  });

  const whitelabels = useMemo(() => {
    // set empty before and during load
    if (!called || whitelabelsLoading) {
      return [];
    }

    // use cache if request failed
    if (!whitelabelsLoading && (error || !data)) {
      return WHITELABELS.cache;
    }

    // use whitelabels from the server
    return data.whitelabels;
  }, [data?.whitelabels, whitelabelsLoading, error]);

  const whitelabelByIndex = useWhitelabelByIndexHTML();

  // set whitelabel loading successful after setting whitelabel object to render app
  const hasWhiteLabelLoaded = useMemo(() => {
    return Boolean((whitelabel as any)?._id);
  }, [whitelabel]);

  const blocks: BlockType[] = useMemo(
    () => blocksResult.data?.contentBlocks || [],
    [blocksResult.data?.contentBlocks]
  );

  const cards = useMemo(
    () =>
      blocksResult.data?.contentCards ? blocksResult.data.contentCards : [],
    [blocksResult.data?.contentCards]
  );

  async function doTransition(
    duration: number,
    onFinishTransition: () => void
  ) {
    try {
      await pause(duration);
      onFinishTransition();
    } catch (err) {
      throw new Error(`Something Went Wrong ${err.message}`);
    } finally {
      setIsTransitioning(false);
    }
  }

  const clearTransition = useCallback(() => {
    setIsTransitioning(false);
  }, []);

  const transition = useCallback(
    (duration = 2500, onFinishTransition = () => {}) => {
      setIsTransitioning(true);
      doTransition(duration, onFinishTransition);
    },
    []
  );

  useEffect(() => {
    // Try to find the whitelabel using the provided findWhiteLabel function
    // For web: this matches based on hostname (e.g., wl2.s.app.joinlane.com -> lwl2)
    // For mobile: this matches based on instance (bundle ID -> whitelabel instance)
    let wl = whitelabels.find(findWhiteLabel);

    // If a whitelabel was found via findWhiteLabel, use it (it's the authoritative match)
    // Only fall back to defaultInstance if no match was found
    if (!wl) {
      wl = whitelabels.find(
        (whitelabel: any) => whitelabel.instance === defaultInstance
      );
    }

    // Only use whitelabelByIndex as fallback when whitelabels array is empty or not loaded yet
    // This ensures we use fresh data from server when available
    if (!wl && __DEV__ && whitelabelByIndex && whitelabels.length === 0) {
      // Only use stale data if we don't have fresh data yet
      wl = whitelabelByIndex;
    }

    setWhitelabel(wl);

    if (!wl && whitelabels.length) {
      lDClient.setAttribute({
        whiteLabelName: wl?.name,
        whiteLabelId: wl?._id,
        whiteLabelInstance: wl?.instance,
      });
    }
  }, [whitelabels, whitelabelsLoading]);

  useEffect(() => {
    fetchWhiteLabels();
    fetchBlocks();
  }, []);

  return {
    whitelabel,
    whitelabels,
    hasWhiteLabelLoaded,
    blocks,
    cards,
    isTransitioning,
    isBlockingLoad,
    setIsBlockingLoad,
    transition,
    clearTransition,
    fetchWhiteLabels,
    fetchBlocks,
    isThemeBlockingLoad,
    setIsThemeBlockingLoad,
  };
}
